import { BaseChart } from './BaseChart.js';

export class KPIChart extends BaseChart {
    constructor({
        // DATOS PRINCIPALES
        value = 0,
        unit = '',
        kpiTitle = 'Indicador',
        subtext = '',

        // COLORES PERSONALIZADOS
        valueColor = null,              // Si es null, usa el default del tema
        cardBackgroundColor = '#FFFFFF',
        textColor = '#64748B',      // Color del título
        subtextColor = '#94A3B8',   // Color del subtexto

        // ESTILO & DISEÑO
        layout = 'modern',              // 'modern' o 'simple'
        themeColor = '#3B82F6',     // Color para iconos y decoraciones

        // TIPOGRAFÍA
        fontFamily = 'sans-serif',
        valueFontSize = 26,
        titleFontSize = 10,
        subtextFontSize = 10,

        // ICONO
        icon = null,
        iconSize = 16,
        iconColor = null,

        // TENDENCIA
        showTrend = false,
        trendValue = 0,
        trendType = 'positive_up',

        // Información inferior derecha
        bottomRightInfo = null,

        // PROPIEDADES BASE
        ...baseProps
    } = {}) {
        super({
            ...baseProps,
            showTooltip: false,
            showLegend: false,
            animationDurationUpdate: 800
        });

        this.value = value;
        this.unit = unit;
        this.kpiTitle = kpiTitle;
        this.subtext = subtext;
        // Colores: Priorizamos los enviados, caso contrario usar modernos
        this.valueColor = valueColor;
        this.cardBackgroundColor = cardBackgroundColor;
        this.textColor = textColor;
        this.subtextColor = subtextColor;
        this.themeColor = themeColor;
        this.iconColor = iconColor || themeColor; // El icono usa el tema si no se especifica color

        this.layout = layout;
        this.fontFamily = fontFamily;
        this.valueFontSize = valueFontSize;
        this.titleFontSize = titleFontSize;
        this.subtextFontSize = subtextFontSize;
        this.icon = icon;
        this.iconSize = iconSize;

        this.showTrend = showTrend;
        this.trendValue = trendValue;
        this.trendType = trendType;
        this.bottomRightInfo = bottomRightInfo;
        this.padding = 10;
    }

    /**
     * Convierte una clase CSS (ej: "pi pi-user") en su carácter Unicode real leyendo los estilos calculados del navegador.
     */
    _resolveIcon(iconString) {
        // Si no es un string o no parece un icono de Prime, devolver tal cual (emoji o texto)
        if (!iconString || typeof iconString !== 'string') return { text: '', font: this.fontFamily };
        const isPrimeIcon = iconString.includes('pi-') || iconString.includes('pi ');
        if (!isPrimeIcon) {
            return { text: iconString, font: this.fontFamily };
        }

        // Si es PrimeIcon, intentamos obtener su código
        try {
            // Creamos un elemento temporal invisible
            const i = document.createElement('i');
            i.className = iconString; // ej: "pi pi-calendar"
            i.style.position = 'absolute';
            i.style.visibility = 'hidden';
            i.style.left = '-9999px'; // Fuera de pantalla
            document.body.appendChild(i);
            // Leemos el pseudo-elemento ::before donde PrimeIcons guarda el código
            const content = window.getComputedStyle(i, '::before').getPropertyValue('content');
            document.body.removeChild(i);
            // El content viene como '"\e927"' (con comillas), las quitamos
            const cleanContent = content.replace(/['"]/g, '');
            // Si falló la lectura, devolvemos un fallback
            if (!cleanContent || cleanContent === 'none') return { text: '?', font: this.fontFamily };
            return {
                text: cleanContent,
                font: 'primeicons'
            };
        } catch (e) {
            return { text: iconString, font: this.fontFamily };
        }
    }

    /**
     * Verifica si el valor es numérico antes de formatear.
     */
    formatValue(value) {
        // Si es un string (ej: "$100"), devolverlo directo sin formatear
        if (typeof value === 'string') return value;
        // Si es nulo o undefined, devolver 0
        if (value == null) return 0;

        // Si es número, formatear
        return new Intl.NumberFormat('es-ES', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0
        }).format(value);
    }

    /**
     * Genera un color de fondo suave basado en el color del tema
     */
    getTintedColor(hex) {
        // Si el fondo de la tarjeta es oscuro, el tinte debe ser distinto
        return hex + '1A'; // 10% de opacidad hexadecimal
    }

    getOptions() {
        const elements = [];
        // FONDO DE TARJETA (Usando tu cardBackgroundColor)
        elements.push({
            type: 'rect',
            left: 'center',
            top: 'center',
            shape: { width: '100%', height: '100%' },
            style: {
                fill: this.cardBackgroundColor,
                shadowBlur: 10,
                shadowColor: 'rgba(0,0,0,0.1)',
                shadowOffsetY: 4,
                r: 12 // Bordes redondeados
            },
            z: 0
        });
        if (this.layout === 'modern') {
            this.renderModernLayout(elements);
        } else {
            this.renderSimpleLayout(elements);
        }
        return {
            backgroundColor: 'transparent',
            graphic: { elements: elements }
        };
    }

    renderModernLayout(elements) {
        const p = this.padding;
        const w = this.chartWidth; // Falta implementar
        elements.push({
            type: 'text',
            left: p,
            top: p,
            style: {
                text: this.kpiTitle.toUpperCase(),
                fill: '#64748B',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: this.fontFamily,
                letterSpacing: 1.5
            }
        });

        if (this.icon) {
            const iconInfo = this._resolveIcon(this.icon);
            elements.push({
                type: 'circle',
                right: p,
                top: p + 2,
                shape: { r: 14 },
                style: {
                    fill: this.themeColor,
                    opacity: 0.15
                }
            });
            elements.push({
                type: 'text',
                right: p + 7,
                top: p + 9,
                style: {
                    text: iconInfo.text,
                    fill: this.themeColor,
                    fontSize: 14,
                    fontFamily: iconInfo.font
                }
            });
        }

        let displayValue = this.formatValue(this.value) + (this.unit || '');
        const valWidth = this.medirTexto(displayValue, this.valueFontSize, 'bold');

        elements.push({
            type: 'text',
            left: p,
            top: 'middle',
            style: {
                text: displayValue,
                fill: this.valueColor || '#1E293B',
                fontSize: this.valueFontSize,
                fontWeight: 700,
                fontFamily: this.fontFamily
            }
        });

        if (this.showTrend && this.trendValue !== 0) {
            const isPositive = this.trendValue > 0;
            const trendIcon = isPositive ? '▲' : '▼';

            let trendColor;
            if (this.trendType === 'positive_up') {
                trendColor = isPositive ? '#22C55E' : '#EF4444';
            } else if (this.trendType === 'positive_down') {
                trendColor = isPositive ? '#EF4444' : '#22C55E';
            } else {
                trendColor = '#1b7aff';
            }

            elements.push({
                type: 'text',
                left: p + valWidth + 10,
                top: 'middle',
                style: {
                    text: `${trendIcon} ${Math.abs(this.trendValue)}%`,
                    fill: trendColor,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: this.fontFamily
                }
            });
        }

        elements.push({
            type: 'line',
            left: p,
            right: p,
            bottom: p + 25,
            shape: { x1: 0, y1: 0, x2: w - p * 2, y2: 0 },
            style: { stroke: '#F1F5F9', lineWidth: 1 }
        });

        if (this.subtext) {
            elements.push({
                type: 'text',
                left: p,
                bottom: p,
                style: {
                    text: this.subtext,
                    fill: '#94A3B8',
                    fontSize: 10,
                    fontFamily: this.fontFamily
                }
            });
        }

        if (this.bottomRightInfo) {
            elements.push({
                type: 'text',
                right: p,
                bottom: p,
                style: {
                    text: this.bottomRightInfo.text,
                    fill: '#64748B',
                    fontSize: 10,
                    fontWeight: 600,
                    fontFamily: this.fontFamily,
                    textAlign: 'right'
                }
            });
        }
    }

    renderSimpleLayout(elements) {
        const p = this.padding;
        elements.push({
            type: 'rect',
            left: 0,
            top: 'center',
            shape: { width: 4, height: 40, r: 2 },
            style: { fill: this.themeColor },
            z: 1
        });
        if (this.icon) {
            const iconInfo = this._resolveIcon(this.icon);
            elements.push({
                type: 'text',
                left: p + 8,
                top: 'center',
                style: {
                    text: iconInfo.text,
                    fill: this.iconColor,
                    fontSize: this.iconSize,
                    fontFamily: iconInfo.font,
                    textBaseline: 'middle'
                }
            });
        }

        const contentLeft = this.icon ? p + 8 + this.iconSize + 8 : p + 12;

        elements.push({
            type: 'text',
            left: contentLeft,
            top: p,
            style: {
                text: this.kpiTitle,
                fill: this.textColor,
                fontSize: this.titleFontSize,
                fontWeight: 600,
                fontFamily: this.fontFamily
            }
        });

        let displayValue = this.formatValue(this.value);
        if (this.unit && typeof this.value === 'number') {
            displayValue += this.unit;
        }

        elements.push({
            type: 'text',
            left: contentLeft,
            top: 'middle',
            style: {
                text: displayValue,
                fill: this.valueColor ? this.valueColor : '#1E293B',
                fontSize: this.valueFontSize,
                fontWeight: 'bold',
                fontFamily: this.fontFamily,
                textBaseline: 'middle'
            }
        });

        if (this.showTrend && this.trendValue !== 0) {
            const isPositive = this.trendValue > 0;
            const trendIcon = isPositive ? '▲' : '▼';

            let trendColor;
            if (this.trendType === 'positive_up') {
                trendColor = isPositive ? '#22C55E' : '#EF4444';
            } else if (this.trendType === 'positive_down') {
                trendColor = isPositive ? '#EF4444' : '#22C55E';
            } else {
                 trendColor = '#1b7aff';
            }

            elements.push({
                type: 'text',
                right: p,
                top: 'middle',
                style: {
                    text: `${trendIcon} ${Math.abs(this.trendValue)}%`,
                    fill: trendColor,
                    fontSize: this.subtextFontSize,
                    fontWeight: 'bold',
                    fontFamily: this.fontFamily,
                    textBaseline: 'middle'
                }
            });
        }

        // SUBTEXTO
        if (this.subtext) {
            elements.push({
                type: 'text',
                left: contentLeft,
                bottom: p + 5,
                style: {
                    text: this.subtext,
                    fill: this.subtextColor,
                    fontSize: this.subtextFontSize,
                    fontFamily: this.fontFamily
                }
            });
        }

        // SEPARADOR INFERIOR
        elements.push({
            type: 'line',
            left: p,
            right: p,
            bottom: p + 20,
            shape: { y1: 0, y2: 0, percent: 1 },
            style: {
                stroke: this.themeColor + '33', // 20% opacidad
                lineWidth: 1
            }
        });

        if (this.bottomRightInfo) {
            const { text, color, bgColor, icon } = this.bottomRightInfo;

            const fontSize = 10;
            const fontWeight = 'bold';
            const innerPad = 10;
            const iconGap = 5;
            const iconSize = 10;

            const txtWidth = text ? this.medirTexto(text, fontSize, fontWeight) : 0;
            const icoWidth = icon ? iconSize : 0;
            const gapWidth = (text && icon) ? iconGap : 0;
            const totalRectWidth = innerPad + txtWidth + gapWidth + icoWidth + innerPad;

            if (bgColor) {
                elements.push({
                    type: 'rect',
                    right: p,
                    bottom: p,
                    shape: { width: totalRectWidth, height: 20, r: 10 },
                    style: { fill: bgColor },
                    z: 1
                });
            }

            let currentOffsetRight = p + innerPad;

            if (icon) {
                const iconObj = this._resolveIcon(icon);
                elements.push({
                    type: 'text',
                    right: currentOffsetRight,
                    bottom: p + 4,
                    z: 2,
                    style: {
                        text: iconObj.text,
                        fill: color || this.textColor,
                        fontSize: iconSize,
                        fontFamily: iconObj.font,
                        textAlign: 'right'
                    }
                });
                currentOffsetRight += icoWidth + gapWidth;
            }

            if (text) {
                elements.push({
                    type: 'text',
                    right: currentOffsetRight,
                    bottom: p + 4,
                    z: 2,
                    style: {
                        text,
                        fill: color || this.textColor,
                        fontSize,
                        fontWeight,
                        fontFamily: this.fontFamily,
                        textAlign: 'right'
                    }
                });
            }
        }
    }

    isDarkColor(hex) {
        if (!hex || hex === 'transparent') return false;
        if (hex[0] === '#') hex = hex.substring(1);
        if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return yiq < 128;
    }

    medirTexto(text, fontSize, fontWeight = 'normal') {
        if (!text) return 0;
        // Creamos un canvas en memoria (no afecta al DOM)
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        // Establecemos la misma fuente que usará ECharts
        context.font = `${fontWeight} ${fontSize}px ${this.fontFamily}`;
        const metrics = context.measureText(text);
        return metrics.width;
    }
}
